import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Container, Div, Text } from "../../styles/Common";
import { useMediaQuery } from "react-responsive";
import { theme } from "../../styles/theme";
import { Link } from "../../components/Link";
import back from "../../assets/icons/arrow-left.svg";
import map from "../../assets/icons/map.svg";
import box from "../../assets/icons/box.svg";
import users from "../../assets/icons/users.svg";
import doc from "../../assets/icons/doc.svg";
import phone from "../../assets/icons/phone.svg";
import mail from "../../assets/icons/email.svg";
import { Row, Col } from "antd";
import { CardLink, DashedLine, IconCard } from "./styles";
import useGetProjectInfo from "./hooks/useGetProjectInfo";
import ReactLoading from "react-loading";

const ApplicationProject = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({
    query: `(max-width: ${theme.breakpoints.sm})`,
  });
  const { id } = useParams();
  const [projectInfo, setProjectInfo] = useState({});
  const { data, isLoading, isSuccess } = useGetProjectInfo(id);
  const [keyContacts, setKeyContacts] = useState([]);

  useEffect(() => {
    if (isSuccess) {
      const contacts = [];
      
      if (data?.data.projectManager) {
        contacts.push({
          name: data?.data.projectManager,
          email: data?.data.projectManagerEmail,
          phone: data?.data.projectManagerPhone,
          role: "Project Manager"
        });
      }
      
      if (data?.data.chiefInspectors) {
        contacts.push(...data?.data.chiefInspectors.map(inspector => ({
          ...inspector,
          role: "Chief Inspector"
        })));
      }
      
      setKeyContacts(contacts);
      setProjectInfo({
        address: data?.data.address,
        coordinates: data?.data.coordinates,
        state: data?.data.state,
        projectName: data?.data.projectName,
      });
    }
  }, [isSuccess, data])
  
  const handleBack = () => {
    navigate(`/application`);
  };

  const quickLinks = [
    {
      id: 1,
      title: "View / Edit Weld Maps",
      description: "Access welding diagrams and inspection maps",
      icon: map,
      background: theme.colors.pastelRed,
    },
    {
      id: 2,
      title: "Project Inventory",
      description: "Manage materials and equipment",
      icon: box,
      background: theme.colors.pastelBlue,
    },
    {
      id: 3,
      title: "Welders",
      description: "View welder assignments and certifications",
      icon: users,
      background: theme.colors.pastelGreen,
    },
    {
      id: 4,
      title: "Documents",
      description: "Project documentation and reports",
      icon: doc,
      background: theme.colors.pastelOrange,
    },
  ];

  return (
    <Container>
      {isLoading ? (
        <Div width="100%" height="100%" justify="center" align="center">
          <ReactLoading color={theme.colors.salmon} />
        </Div>
      ) : (
        <>
          <Div m="0 0 25px 0" height="30px" align="center">
            <Link
              onClick={handleBack}
              color={theme.colors.grey1}
              icon={<img alt="back" src={back} style={{ marginRight: "12px" }} />}
            >
              Back
            </Link>
          </Div>
          <Div width="100%" height="55px" justify="space-between" align="center">
            <Div direction="column">
              <Text
                size={isMobile ? theme.fonts.size.h6 : theme.fonts.size.h4}
                color={theme.colors.dark}
                weight={theme.fonts.weight.bold}
              >
                {projectInfo.projectName}
              </Text>
            </Div>
          </Div>
          <Div direction={isMobile ? "column" : "row"} gap="16px" m="24px 0 0 0">
            <Card 
              width={isMobile ? "100%" : "33%"} 
              height={isMobile ? "auto" : "394px"} 
              padding="19px 23px 28px 22px"
              style={{ justifyContent: "start", alignItems: "flex-start" }}
            >
              <Div direction="column" width="100%">
                <Text color={theme.colors.dark} weight={theme.fonts.weight.bold} size={theme.fonts.size.h6} mb="22px">
                  Project Information
                </Text>
                <Div direction="column" gap="6px">
                  <Text color={theme.colors.dark} size={theme.fonts.size.h6} weight={theme.fonts.weight.medium} mb="2px">
                    Location
                  </Text>
                  <Text color={theme.colors.dark}>
                    {projectInfo.address}
                  </Text>
                  <Text color={theme.colors.dark}>
                    {projectInfo.state}
                  </Text>
                  <Text color={theme.colors.grey2} size={theme.fonts.size.sm} mt="2px">
                    {projectInfo.coordinates}
                  </Text>
                </Div>
                <DashedLine />
                <Text color={theme.colors.dark} weight={theme.fonts.weight.medium} size={theme.fonts.size.h6} mb="8px">
                  Key Contacts
                </Text>
                <Div direction="column" gap="34px">
                  {keyContacts.map((ele, index) => (
                    <Row key={ele.id} gutter={[16, 4]}>
                      <Col md={12} xs={24}>
                        <Text color={theme.colors.dark} size={theme.fonts.size.default}>
                          {ele.name}
                        </Text>
                      </Col>
                      <Col md={12} xs={24}>
                        <Text 
                          color={theme.colors.grey2} 
                          size={theme.fonts.size.sm} 
                          style={{ 
                            display: "flex", 
                            alignItems: "center",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            hyphens: "auto"
                          }}
                        >
                          <img alt="mail" src={mail} style={{ marginRight: "3px", flexShrink: 0 }} />
                          <span style={{ wordBreak: "break-all" }}>{ele.email}</span>
                        </Text>
                      </Col>
                      <Col md={12} xs={24}>
                        <Text color={theme.colors.grey2} size={theme.fonts.size.sm}>
                          {ele.role}
                        </Text>
                      </Col>
                      <Col md={12} xs={24}> 
                        <Text color={theme.colors.grey2} size={theme.fonts.size.sm} style={{ display: "flex", alignItems: "center" }}>
                          <img alt="phone" src={phone} style={{ marginRight: "3px" }} />
                          {ele.phone}
                        </Text>
                      </Col>
                    </Row>
                  ))}
                </Div>
              </Div>
            </Card>
            <Card 
              width={isMobile ? "100%" : "67%"} 
              height={isMobile ? "auto" : "394px"} 
              padding="19px 28px 63px 28px"
              style={{ justifyContent: "start", alignItems: "flex-start" }}
            >
              <Div direction="column">
                <Text color={theme.colors.dark} weight={theme.fonts.weight.bold} size={theme.fonts.size.h6} mb="24px">
                  Quick Links <span style={{ color: theme.colors.salmon, fontWeight: theme.fonts.weight.bold }}>(Coming Soon...)</span>
                </Text>
                <Row gutter={[16, 24]}>
                  {quickLinks.map((link) => (
                    <Col key={link.id} md={12} xs={24}>
                      <CardLink background={link.background}>
                        <IconCard>
                          <img alt="icon" src={link.icon} />
                        </IconCard>
                        <Div direction="column" gap="6px">
                          <Text 
                            color={theme.colors.dark} 
                            weight={theme.fonts.weight.bold} 
                            size={isMobile ? theme.fonts.size.default : theme.fonts.size.h6}
                          >
                            {link.title}
                          </Text>
                          <Text 
                            color={theme.colors.dark} 
                            size={theme.fonts.size.sm}>
                            {link.description}
                          </Text>
                        </Div>
                      </CardLink>
                    </Col>
                  ))}
                </Row>
              </Div>
            </Card>
          </Div>
        </>
      )}
    </Container>
  );
};

export default ApplicationProject;